import { type FormEvent, useState } from "react";
import { BookOpen, Plus } from "lucide-react";
import CleButton from "../../../components/CleButton";
import CleModal, {
  CleModalRoot,
  CleModalTrigger,
} from "../../../components/CleModal";
import { useCreateAuthorship } from "@/hooks/useAuthorships";
import {
  MAX_LESSONS_PER_EVIDENCE,
  isValidLessonCount,
} from "../../../utils/lessonLimits";

const AUTHORSHIP_FORM_ID = "cle-authorship-create-form";

const AuthorshipCreateDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [bookTitle, setBookTitle] = useState("");
  const [publisher, setPublisher] = useState("");
  const [publicationYear, setPublicationYear] = useState("");
  const [isbn, setIsbn] = useState("");
  const [numberOfLessons, setNumberOfLessons] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const createAuthorshipMutation = useCreateAuthorship();

  const resetForm = () => {
    setBookTitle("");
    setPublisher("");
    setPublicationYear("");
    setIsbn("");
    setNumberOfLessons("");
    setValidationError(null);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      createAuthorshipMutation.reset();
      setValidationError(null);

      return;
    }

    resetForm();
  };

  const clearValidationError = () => {
    setValidationError(null);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedBookTitle = bookTitle.trim();
    const trimmedPublisher = publisher.trim();
    const trimmedIsbn = isbn.trim();
    const parsedPublicationYear = Number(publicationYear);
    const parsedLessons = Number(numberOfLessons);
    const currentYear = new Date().getFullYear();

    if (!trimmedBookTitle) {
      setValidationError("Angiv bogens titel.");

      return;
    }

    if (!trimmedPublisher) {
      setValidationError("Angiv forlag.");

      return;
    }

    if (
      !Number.isInteger(parsedPublicationYear) ||
      parsedPublicationYear < 1900 ||
      parsedPublicationYear > currentYear + 1
    ) {
      setValidationError("Angiv et gyldigt udgivelsesår.");

      return;
    }

    if (!isValidLessonCount(parsedLessons)) {
      setValidationError(
        `Angiv et antal lektioner mellem 1 og ${MAX_LESSONS_PER_EVIDENCE}. Decimaler er tilladt og afrundes til to decimaler.`,
      );

      return;
    }

    createAuthorshipMutation.mutate(
      {
        bookTitle: trimmedBookTitle,
        isbn: trimmedIsbn || null,
        numberOfLessons: parsedLessons,
        publicationYear: parsedPublicationYear,
        publisher: trimmedPublisher,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          resetForm();
        },
      },
    );
  };

  return (
    <CleModalRoot
      controller={{
        open: isOpen,
        setOpen: setIsOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <CleModalTrigger asChild>
        <CleButton id="cle-create-authorship" variant="outline" size="sm">
          <Plus size={17} strokeWidth={2} />
          Opret forfatterskab
        </CleButton>
      </CleModalTrigger>
      <CleModal
        title="Opret forfatterskab"
        description="Angiv oplysninger om udgivelsen og antal lektioner."
        size="sm"
        footer={
          <div className="d-flex justify-content-center gap-3 w-100 px-3 py-2">
            <CleButton
              id="cle-cancel-authorship-create"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createAuthorshipMutation.isPending}
            >
              Afbryd
            </CleButton>
            <CleButton
              id="cle-submit-authorship-create"
              type="submit"
              form={AUTHORSHIP_FORM_ID}
              disabled={createAuthorshipMutation.isPending}
            >
              {createAuthorshipMutation.isPending ? "Opretter..." : "Opret"}
            </CleButton>
          </div>
        }
      >
        <form
          id={AUTHORSHIP_FORM_ID}
          className="d-flex flex-column gap-3"
          onSubmit={handleSubmit}
        >
          <div className="d-flex flex-column align-items-center text-center gap-3">
            <span
              className="cle-compliance-doc-dashboard__empty-icon mb-0"
              aria-hidden="true"
            >
              <BookOpen size={24} strokeWidth={1.75} />
            </span>
            <div>
              <h3 className="fs-5 fw-semibold mb-2">Opret forfatterskab</h3>
              <p className="text-muted mb-0">
                Udfyld oplysningerne om udgivelsen, så den kan indgå i din
                efteruddannelse.
              </p>
            </div>
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-authorship-book-title"
            >
              Bogtitel
            </label>
            <input
              id="cle-authorship-book-title"
              className="form-control"
              type="text"
              value={bookTitle}
              onChange={(event) => {
                setBookTitle(event.target.value);
                clearValidationError();
              }}
              disabled={createAuthorshipMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-authorship-publisher"
            >
              Forlag
            </label>
            <input
              id="cle-authorship-publisher"
              className="form-control"
              type="text"
              value={publisher}
              onChange={(event) => {
                setPublisher(event.target.value);
                clearValidationError();
              }}
              disabled={createAuthorshipMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-authorship-publication-year"
            >
              Udgivelsesår
            </label>
            <input
              id="cle-authorship-publication-year"
              className="form-control"
              type="number"
              inputMode="decimal"
              min="1900"
              max={new Date().getFullYear() + 1}
              step="any"
              value={publicationYear}
              onChange={(event) => {
                setPublicationYear(event.target.value);
                clearValidationError();
              }}
              disabled={createAuthorshipMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-authorship-isbn"
            >
              ISBN
            </label>
            <input
              id="cle-authorship-isbn"
              className="form-control"
              type="text"
              value={isbn}
              onChange={(event) => {
                setIsbn(event.target.value);
                clearValidationError();
              }}
              disabled={createAuthorshipMutation.isPending}
            />
          </div>

          <div>
            <label
              className="form-label fw-semibold"
              htmlFor="cle-authorship-lessons"
            >
              Antal lektioner
            </label>
            <input
              id="cle-authorship-lessons"
              className="form-control"
              type="number"
              inputMode="decimal"
              min="1"
              max={MAX_LESSONS_PER_EVIDENCE}
              step="any"
              value={numberOfLessons}
              onChange={(event) => {
                setNumberOfLessons(event.target.value);
                clearValidationError();
              }}
              disabled={createAuthorshipMutation.isPending}
            />
          </div>

          {validationError ? (
            <div className="alert alert-warning mb-0" role="alert">
              {validationError}
            </div>
          ) : null}

          {createAuthorshipMutation.isError ? (
            <div className="alert alert-warning mb-0" role="alert">
              Forfatterskabet kunne ikke oprettes lige nu. Prøv igen.
            </div>
          ) : null}
        </form>
      </CleModal>
    </CleModalRoot>
  );
};

export default AuthorshipCreateDialog;
